import { Smile } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { Button } from './button';
import { EMOJI_CATEGORIES } from './emoji-data';
import { Input } from './input';
import { Popover, PopoverContent, PopoverTrigger } from './overlays';

/** Searchable emoji grid. */
export function EmojiPanel({ onSelect }: { onSelect: (emoji: string) => void }) {
  const [query, setQuery] = useState('');
  const categories = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return EMOJI_CATEGORIES;
    const seen = new Set<string>();
    const matches = EMOJI_CATEGORIES.flatMap((c) => c.emojis).filter((e) => {
      if (seen.has(e.char) || !e.name.includes(q)) return false;
      seen.add(e.char);
      return true;
    });
    return [{ name: 'Results', emojis: matches }];
  }, [query]);

  return (
    <div className="w-72">
      <Input
        autoFocus
        placeholder="Search emoji"
        aria-label="Search emoji"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="mb-2"
      />
      <div className="h-64 overflow-y-auto pr-1">
        {categories.map((category) => (
          <section key={category.name}>
            <h3 className="sticky top-0 bg-surface py-1 text-2xs font-semibold tracking-wide text-fg-subtle uppercase">
              {category.name}
            </h3>
            {category.emojis.length === 0 ? (
              <p className="py-6 text-center text-sm text-fg-muted">No emoji found</p>
            ) : (
              <div className="grid grid-cols-8">
                {category.emojis.map((e) => (
                  <button
                    key={`${category.name}-${e.char}`}
                    type="button"
                    title={e.name}
                    aria-label={e.name}
                    onClick={() => onSelect(e.char)}
                    className="flex size-8 items-center justify-center rounded-md text-lg hover:bg-hover"
                  >
                    {e.char}
                  </button>
                ))}
              </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}

export function EmojiPicker({
  onSelect,
  children,
  closeOnSelect = true,
}: {
  onSelect: (emoji: string) => void;
  /** Custom trigger. Defaults to a ghost smile button. */
  children?: ReactNode;
  closeOnSelect?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children ?? (
          <Button size="icon-sm" variant="ghost" aria-label="Add emoji">
            <Smile />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent>
        <EmojiPanel
          onSelect={(emoji) => {
            onSelect(emoji);
            if (closeOnSelect) setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
